// Tipi e helper condivisi per la sezione Assistenza (ticket).

export type TicketTipo = 'MIGLIORIA' | 'ERRORE' | 'SUPPORTO';
export type TicketPriorita = 'BLOCCANTE' | 'URGENTE' | 'MEDIA' | 'BASSA';
export type TicketStato = 'APERTO' | 'IN_LAVORAZIONE' | 'RISOLTO' | 'CHIUSO';

export const TICKET_TIPO_LABEL: Record<TicketTipo, string> = {
  MIGLIORIA: 'Miglioria',
  ERRORE: 'Errore',
  SUPPORTO: 'Supporto',
};

export const TICKET_PRIORITA_LABEL: Record<TicketPriorita, string> = {
  BLOCCANTE: 'Bloccante',
  URGENTE: 'Urgente',
  MEDIA: 'Media',
  BASSA: 'Bassa',
};

export const TICKET_STATO_LABEL: Record<TicketStato, string> = {
  APERTO: 'Aperto',
  IN_LAVORAZIONE: 'In lavorazione',
  RISOLTO: 'Risolto',
  CHIUSO: 'Chiuso',
};

export function ticketPrioritaBadge(p: TicketPriorita): string {
  switch (p) {
    case 'BLOCCANTE': return 'bop-badge--danger';
    case 'URGENTE': return 'bop-badge--warning';
    case 'MEDIA': return 'bop-badge--info';
    case 'BASSA': return 'bop-badge--neutral';
  }
}

export function ticketStatoBadge(s: TicketStato): string {
  switch (s) {
    case 'APERTO': return 'bop-badge--info';
    case 'IN_LAVORAZIONE': return 'bop-badge--warning';
    // Risolto non è un traguardo: aspetta una risposta, quindi non è verde
    case 'RISOLTO': return 'bop-badge--orange';
    case 'CHIUSO': return 'bop-badge--neutral';
  }
}

/**
 * Etichetta dello stato che dice di chi è il turno. RISOLTO significa che lo
 * staff ha finito e la richiesta aspetta la conferma di chi l'ha aperta: per
 * quella persona è "Da confermare", per lo staff è "Attesa conferma".
 */
export function ticketStatoTesto(s: TicketStato, staffAssistenza = false): string {
  if (s === 'RISOLTO') return staffAssistenza ? 'Attesa conferma' : 'Da confermare';
  return TICKET_STATO_LABEL[s];
}

/** Un ticket è attivo finché non è chiuso: un risolto in attesa di conferma lo è. */
export function ticketAttivo(s: TicketStato): boolean {
  return s !== 'CHIUSO';
}

export interface TicketSocieta { id: number; nome: string; }

export interface TicketSummary {
  id: number;
  tipo: TicketTipo;
  priorita: TicketPriorita;
  stato: TicketStato;
  titolo: string;
  modulo: string | null;
  urlPagina: string | null;
  createdByName: string;
  createdAt: string;
  societa: TicketSocieta | null;
  _count?: { allegati: number; risposte: number };
}

export interface TicketAllegato {
  id: number;
  nomeFile: string;
  mimeType: string | null;
  dimensione: number | null;
  uploadedAt: string;
}

export interface TicketRisposta {
  id: number;
  utenteId: number;
  utenteName: string;
  testo: string;
  isStaff: boolean;
  interna?: boolean; // nota per lo staff: il backend la manda solo al SUPERADMIN
  createdAt: string;
}

export interface TicketDettaglio extends TicketSummary {
  descrizione: string;
  createdById: number;
  updatedAt: string;
  chiusoAt: string | null;
  risoltoAt: string | null;           // da quando aspetta la conferma di chi ha aperto
  remindConfermaCount: number;        // promemoria di conferma già inviati
  allegati: TicketAllegato[];
  risposte: TicketRisposta[];
  chat?: ChatTrascrizione | null;
}

// ── Chat con l'assistente ──
export type ChatEsitoTipo = 'DOMANDA' | 'RISPOSTA' | 'PROPOSTA_TICKET';

export interface TicketProposto {
  tipo: TicketTipo;
  priorita: TicketPriorita;
  titolo: string;
  descrizione: string;
  modulo: string | null;
}

export interface ChatEsito {
  esito: ChatEsitoTipo;
  testo: string;
  fonti: string[];
  ticket?: TicketProposto;
}

export interface ChatAllegato {
  indice: number;
  nomeFile: string;
  mimeType: string | null;
  dimensione: number | null;
}

export interface ChatMessaggio {
  id: number;
  ruolo: 'UTENTE' | 'ASSISTENTE';
  testo: string;
  esito?: ChatEsito | null;
  allegati?: ChatAllegato[];
  createdAt: string;
}

export interface ChatTrascrizione {
  id: number;
  stato: 'APERTA' | 'RISOLTA' | 'TICKET' | 'ABBANDONATA';
  documentiKb: string[];
  messaggi: (Pick<ChatMessaggio, 'id' | 'ruolo' | 'testo' | 'createdAt'> & { allegati?: { nomeFile: string }[] | null })[];
}
